import { formatUsd } from '../data/countries';
import {
  getSubmissionListingMode,
  formatSubmissionListingModeLabel,
  formatSubmissionStatusLabel,
  getSubmissionDeletionActor,
  submissionMatchesSearch,
  getSubmissionPhotoUrls,
} from './submissions';

export const DELETED_LISTINGS_METRICS = [
  { key: 'fractionalBuy', label: 'Fractional buy' },
  { key: 'forRent', label: 'For rent' },
  { key: 'deletedByAdmin', label: 'Deleted by admin' },
  { key: 'deletedByLister', label: 'Deleted by lister' },
  { key: 'hadPublishedProperty', label: 'Was live on site' },
  { key: 'pipelineOnly', label: 'Pipeline only' },
];

export const DELETED_LISTINGS_CHART_COLORS = {
  fractionalBuy: '#102734',
  forRent: '#d4b06f',
  deletedByAdmin: '#dc3545',
  deletedByLister: '#64748b',
  hadPublishedProperty: '#28a745',
  pipelineOnly: '#bf9a52',
};

const parseDateOnlyParts = (value) => {
  const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const parseDeletedAt = (row) => {
  const raw = row?.deletedAt;
  if (raw) {
    let d = null;
    if (typeof raw?.toDate === 'function') {
      d = raw.toDate();
    } else if (raw?.seconds != null) {
      d = new Date(raw.seconds * 1000);
    } else if (raw?._seconds != null) {
      d = new Date(raw._seconds * 1000);
    } else if (typeof raw === 'string') {
      d = parseDateOnlyParts(raw) || new Date(raw);
    } else if (typeof raw === 'number') {
      d = new Date(raw);
    }
    if (d && !Number.isNaN(d.getTime())) return d;
  }
  if (row?.deletedAtIso) {
    const d = parseDateOnlyParts(row.deletedAtIso) || new Date(row.deletedAtIso);
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (row?.deletedDate) {
    const d = parseDateOnlyParts(row.deletedDate) || new Date(row.deletedDate);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
};

export const getSubmissionDeletedAt = parseDeletedAt;

export const deletedMatchesDateFilter = (row, { mode, month, date, year } = {}) => {
  if (!mode || mode === 'all') return true;
  const deleted = parseDeletedAt(row);
  if (!deleted) return false;

  if (mode === 'year') {
    if (!year) return true;
    const y = Number(year);
    if (!y) return true;
    return deleted.getFullYear() === y;
  }
  if (mode === 'month') {
    if (!month) return true;
    const [y, mon] = month.split('-').map((part) => Number(part));
    if (!y || !mon) return true;
    return deleted.getFullYear() === y && deleted.getMonth() + 1 === mon;
  }
  if (mode === 'date') {
    if (!date) return true;
    const [y, mon, day] = date.split('-').map((part) => Number(part));
    if (!y || !mon || !day) return true;
    return deleted.getFullYear() === y
      && deleted.getMonth() + 1 === mon
      && deleted.getDate() === day;
  }
  return true;
};

export const adminDeletedListingMatchesSearch = (row, query) => {
  if (submissionMatchesSearch(row, query)) return true;
  const deletion = getSubmissionDeletionActor(row);
  const haystack = [
    row.title,
    row.location,
    row.type,
    row.agentEmail,
    row.submitterEmail,
    row.status,
    formatSubmissionStatusLabel(row.status),
    deletion.email,
    deletion.role,
    row.publishedPropertyId,
  ].filter(Boolean).join(' ').toLowerCase();
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  return haystack.includes(q);
};

export const computeDeletedListingsMetrics = (rows) => {
  const list = rows || [];
  return {
    fractionalBuy: list.filter((row) => getSubmissionListingMode(row) === 'sale').length,
    forRent: list.filter((row) => getSubmissionListingMode(row) === 'rent').length,
    deletedByAdmin: list.filter((row) => getSubmissionDeletionActor(row).role === 'admin').length,
    deletedByLister: list.filter((row) => getSubmissionDeletionActor(row).role === 'lister').length,
    hadPublishedProperty: list.filter((row) => !!row.publishedPropertyId).length,
    pipelineOnly: list.filter((row) => !row.publishedPropertyId).length,
  };
};

const csvEscape = (value) => {
  const s = String(value ?? '');
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const rowToCsvRecord = (row) => {
  const deletion = getSubmissionDeletionActor(row);
  const deleted = parseDeletedAt(row);
  const isRent = getSubmissionListingMode(row) === 'rent';
  const photos = getSubmissionPhotoUrls(row);

  return {
    id: row.id || '',
    title: row.title || '',
    location: row.location || '',
    country: row.country || '',
    listingMode: getSubmissionListingMode(row),
    listingModeLabel: formatSubmissionListingModeLabel(row, { short: false }),
    type: row.type || '',
    priceUsd: Number(row.price) || 0,
    monthlyRentUsd: isRent && Number(row.monthlyRent) > 0 ? Number(row.monthlyRent) : '',
    targetDisplay: isRent && Number(row.monthlyRent) > 0
      ? `${formatUsd(Number(row.monthlyRent))}/mo`
      : `$${Number(row.price || 0).toLocaleString()}`,
    pipelineStatus: formatSubmissionStatusLabel(row.status),
    deletedAt: deleted ? deleted.toISOString() : '',
    deletedDate: row.deletedDate || '',
    deletedByRole: deletion.role || '',
    deletedByEmail: deletion.email || '',
    listerEmail: row.agentEmail || row.submitterEmail || '',
    publishedPropertyId: row.publishedPropertyId || '',
    imageCount: photos.length,
    imageUrls: photos.join(' | '),
  };
};

export const downloadDeletedListingsCsv = (rows, { filenamePrefix = 'coown-deleted-listings' } = {}) => {
  const headers = [
    'id',
    'title',
    'location',
    'country',
    'listingMode',
    'listingModeLabel',
    'type',
    'priceUsd',
    'monthlyRentUsd',
    'targetDisplay',
    'pipelineStatus',
    'deletedAt',
    'deletedDate',
    'deletedByRole',
    'deletedByEmail',
    'listerEmail',
    'publishedPropertyId',
    'imageCount',
    'imageUrls',
  ];
  const records = (rows || []).map(rowToCsvRecord);
  const lines = [headers.map(csvEscape).join(',')];
  records.forEach((record) => {
    lines.push(headers.map((header) => csvEscape(record[header])).join(','));
  });
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const stamp = new Date().toISOString().slice(0, 10);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filenamePrefix}-${stamp}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
